import { FC, useEffect, useState } from "react";
import styled from "styled-components";
import { color } from "@styles";
import { mobile, tablet } from "components/templates/Breakpoints";
import SelectInput from "./SelectInput";
import { Category } from "pages/resource-hub";
import { useRouter } from "next/router";
import { ContentType, localiseContentType } from "@utils";
import { YuLifeLocale } from "@hooks";

export interface ResourceBarProps {
  contentType?: ContentType;
  title?: string;
  path?: string;
  slug?: string;
  categories: Category[];
  locale: YuLifeLocale;
}

export const ResourceBar: FC<ResourceBarProps> = ({
  contentType,
  title,
  categories,
  slug,
  path,
  locale,
}) => {
  const router = useRouter();
  const initialOption = slug || "";
  const [selectedOption, setSelectedOption] = useState(initialOption);
  const updatedCategories = initialOption
    ? [{ name: "All Categories", slug: "all-categories" }, ...categories]
    : categories;

  const handleSelectChange = (value: string, path: string) => {
    const sanitisedPath = path.replaceAll("/", "");

    if (value === "all-categories") {
      return router.push(`/${sanitisedPath}/`);
    }

    router.push(`/${sanitisedPath}/category/${value}/`);
  };

  useEffect(() => {
    setSelectedOption(slug || "");
  }, [slug]);

  return (
    <Bar>
      <Centered>
        <ContentContainer>
          <Title>{title ? title : localiseContentType(contentType, locale)}</Title>
          {/* We are currently not using categories/blog tags in Japan */}
          {locale !== "jp" ? (
            <SelectInput
              path={path}
              options={updatedCategories}
              value={selectedOption}
              handleSelectChange={handleSelectChange}
            />
          ) : null}
        </ContentContainer>
      </Centered>
    </Bar>
  );
};

const Bar = styled.div`
  padding: 0 10.5%;
  background: ${color.S3100};
  margin-top: 1rem;

  ${tablet} {
    padding: 0 6%;
    margin-top: 0;
  }

  ${mobile} {
    align-items: center;
    padding: 0 1.5rem;
  }
`;

const Centered = styled.div`
  max-width: 70.5rem;
  margin: 0 auto;
`;

const ContentContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;

  ${mobile} {
    flex-direction: column;
    gap: 1rem;
  }
`;

const Title = styled.h1`
  font-size: 3.5rem;
  letter-spacing: 0rem;
  color: ${color.white};
  margin: 0;

  ${mobile} {
    font-size: 2.5rem;
  }
`;
